import { Component, type ErrorInfo, type ReactNode } from 'react';
import * as Sentry from '@sentry/react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Link } from 'react-router-dom';
import { isChunkLoadError, canAttemptChunkReload, attemptChunkReload, forceReloadNow } from '@/lib/chunkReload';

/**
 * Per-page error boundary — menší než global ErrorBoundary, in-page (sidebar
 * a navigation zůstanou viditelné). Použít kolem každého <Route element={...}>
 * komponenty v AnimatedRoutes.
 *
 * Global ErrorBoundary v App.tsx zůstává jako fallback pro framework-level errors.
 */

interface Props {
  children: ReactNode;
  pageName?: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
  recovering: boolean;
}

export class PageErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null, recovering: false };

  static getDerivedStateFromError(error: Error): State {
    // Stale-chunk po deployi → poslední záchrana: jednorázový cache-busting reload.
    // recovering=true → vykreslíme prázdno (stránka se hned přenačítá), ne chybu.
    const recovering = isChunkLoadError(error) && canAttemptChunkReload();
    return { hasError: true, error, recovering };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    if (isChunkLoadError(error)) {
      // Stale chunk — zkus se zotavit reloadem, NElogovat jako crash do Sentry.
      if (this.state.recovering) attemptChunkReload();
      return;
    }
    console.error(`[PageErrorBoundary:${this.props.pageName ?? 'unknown'}]`, error, info.componentStack);
    Sentry.captureException(error, {
      tags: { page: this.props.pageName ?? 'unknown' },
      extra: { componentStack: info.componentStack },
    });
  }

  handleReset = () => {
    // U stale-chunk chyby nemá smysl jen re-render (znovu naimportuje mrtvý chunk) —
    // vynutit čerstvé načtení dokumentu.
    if (isChunkLoadError(this.state.error)) { forceReloadNow(); return; }
    this.setState({ hasError: false, error: null, recovering: false });
  };

  render() {
    // Probíhá cache-busting reload kvůli stale chunku → nic nevykresluj (zabraň bliknutí chyby).
    if (this.state.recovering) return null;

    if (this.state.hasError) {
      return (
        <div className="p-6 animate-fade-in">
          <Card className="max-w-2xl mx-auto border-destructive/30 bg-destructive/5">
            <CardContent className="p-8 text-center space-y-4">
              <AlertTriangle className="h-12 w-12 text-destructive mx-auto" />
              <div>
                <h2 className="text-xl font-semibold">Stránku nelze zobrazit</h2>
                {this.props.pageName && (
                  <p className="text-sm text-muted-foreground mt-1">Sekce: {this.props.pageName}</p>
                )}
              </div>
              <p className="text-sm text-muted-foreground">
                Něco se pokazilo při načítání obsahu. Zkuste obnovit, nebo se vraťte na hlavní stránku.
              </p>
              {this.state.error?.message && (
                <details className="text-left">
                  <summary className="text-xs text-muted-foreground cursor-pointer hover:text-foreground">
                    Technický detail
                  </summary>
                  <pre className="text-xs bg-muted/30 p-3 rounded mt-2 overflow-x-auto">
                    {this.state.error.message}
                  </pre>
                </details>
              )}
              <div className="flex gap-3 justify-center pt-2">
                <Button variant="outline" onClick={this.handleReset}>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Zkusit znovu
                </Button>
                <Button asChild>
                  <Link to="/">
                    <Home className="h-4 w-4 mr-2" />
                    Domů
                  </Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}
